import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { useTranslation } from 'react-i18next';
import { TooltipIcon } from '../../components/TooltipIcon/TooltipIcon.tsx';
import type { ResultsTileBadge, ResultsTileRightItem, ResultsTileStat } from '../../utils/types.ts';

const toBarWidthPercent = (value: number, maxValue: number): string => {
  const rawValue = Number(value);
  const rawMax = Number(maxValue);
  const pct =
    Number.isFinite(rawValue) && Number.isFinite(rawMax) && rawMax > 0
      ? Math.max(0, Math.min((rawValue / rawMax) * 100, 100))
      : 0;
  return `${pct}%`;
};

export type DealPromptResultsTileProps = {
  title: string;
  badge: ResultsTileBadge;
  stats: ResultsTileStat[];
  rightSectionTitle: string;
  rightSectionActionLabel?: string;
  onRightSectionAction?: () => void;
  rightSectionItems: ResultsTileRightItem[];
  rightSectionMaxItems?: number;
  rightSectionMaxValue?: number;
  barColor?: string;
  reviewSetDetailsLabel: string;
  onReviewSetDetails: () => void;
  onCreateDeal: () => void;
  dataTest?: string;
  createDealPendoFeature?: string;
  reviewSetDetailsPendoFeature?: string;
};

export const DealPromptResultsTile = ({
  title,
  badge,
  stats,
  rightSectionTitle,
  rightSectionActionLabel,
  onRightSectionAction,
  rightSectionItems,
  rightSectionMaxItems,
  rightSectionMaxValue,
  barColor = 'charts.indigo',
  reviewSetDetailsLabel,
  onReviewSetDetails,
  onCreateDeal,
  dataTest,
  createDealPendoFeature,
  reviewSetDetailsPendoFeature,
}: DealPromptResultsTileProps) => {
  const { t } = useTranslation();
  const showBars = rightSectionMaxValue !== undefined && rightSectionMaxValue > 0;

  return (
    <Card
      role="region"
      sx={{ boxShadow: ELEVATION.elevation0, borderRadius: '4px', width: '100%' }}
      data-test={dataTest ?? 'deal-prompt-results-tile'}
    >
      <CardContent
        sx={{ width: '100%', p: 0, paddingBottom: '0 !important', display: 'flex', flexDirection: 'column' }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', px: 2, py: 2, justifyContent: 'space-between', minHeight: '64px' }}
        >
          <Typography variant="h1">{title}</Typography>

          <Box
            sx={{
              height: 22,
              borderRadius: '50px',
              backgroundColor: badge.bgColor,
              display: 'flex',
              alignItems: 'center',
              px: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="overline" sx={{ color: badge.color }}>
              {badge.text}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        <Grid container sx={{ flex: 1 }}>
          <Grid
            size={4}
            sx={{
              py: 3,
              px: 2,
            }}
          >
            <Grid container spacing={1.5}>
              {stats.map(stat => (
                <Grid size={6} key={stat.dataTest ?? `${stat.label}-${stat.value}`} data-test={stat.dataTest}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '4px',
                      boxShadow: ELEVATION.elevation0,
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', lineHeight: 2 }}
                      >
                        {stat.label}
                        {stat.tooltip && <TooltipIcon title={stat.tooltip} />}
                      </Typography>
                      <Typography variant="h1" component="p" sx={{ color: stat.valueColor }}>
                        {stat.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={8} sx={{ py: 3, px: 2, minWidth: 0 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1Bold">{rightSectionTitle}</Typography>
              {rightSectionActionLabel && onRightSectionAction && (
                <Button
                  data-test="right-section-action-button"
                  variant="text"
                  sx={{ color: 'primary.main' }}
                  onClick={onRightSectionAction}
                >
                  {rightSectionActionLabel}
                </Button>
              )}
            </Stack>

            <Stack spacing={0.75}>
              {(rightSectionMaxItems !== undefined
                ? rightSectionItems.slice(0, rightSectionMaxItems)
                : rightSectionItems
              ).map(item => (
                <Stack
                  key={item.dataTest ?? `${item.label}-${item.value}`}
                  direction="row"
                  sx={{ alignItems: 'center', gap: 0.75, width: '100%', overflow: 'hidden' }}
                  data-test={item.dataTest}
                >
                  <Tooltip title={item.label} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 2,
                        minWidth: 0,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Tooltip>

                  {showBars && (
                    <Box sx={{ flex: 5, minWidth: 0 }}>
                      <Box
                        data-test="results-tile-bar"
                        sx={{
                          height: 10,
                          backgroundColor: barColor,
                          width: toBarWidthPercent(item.value, rightSectionMaxValue!),
                        }}
                      />
                    </Box>
                  )}

                  <Typography
                    variant="body2Bold"
                    sx={{
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      textAlign: 'right',
                      ...(showBars ? { minWidth: '4.5ch' } : { ml: 'auto' }),
                    }}
                  >
                    {item.formattedValue ?? String(item.value)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ width: '100%', mx: 2, alignSelf: 'center' }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 3,
            alignSelf: 'center',
            gap: 1,
          }}
        >
          <Button
            data-test="see-details-button"
            data-pendo-feature={reviewSetDetailsPendoFeature}
            variant="text"
            sx={{ color: 'primary.main' }}
            onClick={onReviewSetDetails}
          >
            {reviewSetDetailsLabel}
          </Button>

          <Button
            data-test="create-deal-button"
            data-pendo-feature={createDealPendoFeature}
            color="primary"
            variant="contained"
            onClick={onCreateDeal}
          >
            {t('Create deal')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
